import { computed, nextTick, onMounted, onUnmounted, watch } from "vue";
import { isBackgroundTaskActive } from "~/features/background-tasks/utils/taskPresentation";
import { exportUrl } from "~/utils/apiUrl";
import Permissions from "~/types/Permissions";
import { downloadFileFromBlob } from "~/utils/download";
import { extractFilenameFromContentDisposition } from "~/utils/reportingExport";
import { registerTaskKind, useBackgroundTasks } from "~/features/background-tasks/composables/useBackgroundTasks";
import { ExportResponseError, parseExportAck, parseExportStatus } from "~/features/background-tasks/utils/exportResponses";

/** Server-owned invoice export status; files expire after 24 hours. */
export type InvoiceExportJob = ReturnType<typeof parseExportStatus>;

const EXPIRED_EXPORT_MESSAGE = "Ce fichier n’est plus disponible. Les fichiers sont conservés pendant 24 h après leur création.";

function createInvoiceExportState(scope = ""): {
  scope: string;
  restored?: boolean;
  job: InvoiceExportJob | null;
  autoDownload: boolean;
  autoAttempted: boolean;
  downloaded: boolean;
  downloadFailed: boolean;
  downloading: string | null;
  retrying: string | null;
  cancelling: boolean;
  conflict: boolean;
  interrupted: boolean;
  message: string;
} {
  return {
    scope, job: null, autoDownload: false, autoAttempted: false,
    downloaded: false, downloadFailed: false, downloading: null, retrying: null, cancelling: false,
    conflict: false, interrupted: false, message: "",
  };
}

function useInvoiceExportController() {
  const client = useSanctumClient();
  const { $eventBus, $hasPermission } = useNuxtApp();
  const { scope, tasks, activeTasks, invalidSnapshot, upsertTask, removeTask } = useBackgroundTasks();
  const state = useState("invoice-export-job", () => createInvoiceExportState());
  const job = computed(() => isCurrentScope(state.value.scope) ? state.value.job : null);
  const isActive = computed(() => isBackgroundTaskActive(job.value));
  const downloading = computed(() => state.value.downloading);
  const retrying = computed(() => state.value.retrying);
  let publishedTerminal = job.value && !isActive.value ? `${scope.value}:${job.value.export_id}:${job.value.status}:${state.value.downloadFailed}` : "";

  function isCurrentScope(context: string) {
    return scope.value === context;
  }

  function expireExport(error: unknown, id: string) {
    const status = (error as { response?: { status: number } }).response?.status;
    const expired = status === 404 || status === 410;
    if (expired && job.value?.export_id === id) state.value.job!.status = "expired";
    return expired;
  }

  function reportActionError(error: unknown, id: string, message: string) {
    const expired = expireExport(error, id);
    const summary = expired ? EXPIRED_EXPORT_MESSAGE : message;
    // Bell actions may target an older export; only the tracked job owns the tray message.
    if (job.value?.export_id === id) state.value.message = summary;
    $eventBus.emit("toast", { severity: "error", summary, life: 4000 });
    return expired;
  }

  function publishTask() {
    const current = job.value;
    if (!current) return;
    // downloadFailed is in the key so a failure republishes a ready row the tray already expired.
    const terminal = isActive.value ? "" : `${scope.value}:${current.export_id}:${current.status}:${state.value.downloadFailed}`;
    if (terminal && terminal === publishedTerminal && !tasks.value.some(task => task.id === current.export_id)) return;
    publishedTerminal = terminal;
    let message = state.value.message;
    if (current.status === "expired") message = EXPIRED_EXPORT_MESSAGE;
    else if (state.value.cancelling) message = "Annulation en cours · en attente de confirmation du serveur";
    else if (isActive.value && message) message = `Dernière progression : ${current.processed.toLocaleString("fr-FR")} / ${current.total.toLocaleString("fr-FR")} factures. ${message}`;
    else if (state.value.conflict && isActive.value) message = "Un export est déjà en cours · vous pouvez quitter la page";
    else if (isActive.value && current.total > 0 && current.processed >= current.total) message = "Finalisation du fichier · vous pouvez quitter la page";
    // A failed download keeps its error in the tray instead of the ready summary.
    else if (current.status === "ready" && !state.value.downloadFailed) message = `${current.total.toLocaleString("fr-FR")} factures · Excel · ${state.value.downloaded ? "téléchargement lancé" : "fichier prêt"}`;
    upsertTask({
      id: current.export_id, kind: current.type, scope: scope.value,
      label: "Export des factures", status: current.status === "expired" ? "failed" : current.status,
      processed: current.processed, total: current.total, message,
      // Server retry needs the export permission; the active-job status check does not use this flag.
      restored: state.value.restored, retryable: current.status === "expired" || !$hasPermission(Permissions.AppInvoicesExport) ? false : current.retryable,
      keep: current.status === "ready" && state.value.downloadFailed,
    });
    // BackgroundTaskTray announces and removes a cancelled row in this flush; the fallback covers layouts without it.
    if (current.status === "cancelled") {
      const context = scope.value;
      void nextTick(() => removeTask(current.export_id, context));
    }
  }

  function trackExport(data: Partial<InvoiceExportJob>, autoDownload = false, conflict = false, restored = false) {
    if (!data.export_id) throw new Error("Invoice export acknowledgement missing export_id");
    if (job.value?.export_id === data.export_id && isActive.value) {
      state.value.conflict = conflict;
      state.value.autoDownload ||= autoDownload;
      // The monitor re-arms polling when the interruption clears (see its `interrupted` watcher).
      state.value.interrupted = false;
      publishTask();
      return;
    }
    if (job.value) removeTask(job.value.export_id);
    state.value = {
      ...createInvoiceExportState(scope.value), restored,
      job: { ...data, type: "invoices", export_id: data.export_id, status: data.status ?? "queued", processed: data.processed ?? 0, total: data.total ?? 0 },
      autoDownload, conflict,
    };
    publishTask();
  }

  function stopTracking(message: string) {
    state.value.interrupted = true;
    state.value.message = message;
  }

  function rejectResponse() {
    stopTracking("Réponse inattendue du serveur");
    state.value.cancelling = false;
    $eventBus.emit("toast", { severity: "error", summary: state.value.message, life: 4000 });
  }

  /** Download an owned export without marking it read; report expired files to the bell. */
  async function downloadExport(id: string, fileName?: string) {
    if (state.value.downloading) return false;
    const context = scope.value;
    const current = state.value;
    current.downloading = id;
    if (job.value?.export_id === id) current.message = "";
    try {
      const response = await client.raw(exportUrl.download(id), { responseType: "blob", timeout: 60_000 });
      if (!isCurrentScope(context)) return;
      const filename = extractFilenameFromContentDisposition(response.headers.get("content-disposition"))
        || fileName || (job.value?.export_id === id ? job.value.file_name : null) || "factures.xlsx";
      downloadFileFromBlob(response._data as Blob, filename);
      if (job.value?.export_id === id) {
        current.downloaded = true;
        current.downloadFailed = false;
      }
      return true;
    }
    catch (error) {
      if (!isCurrentScope(context)) return;
      const expired = reportActionError(error, id, "Le téléchargement n’a pas démarré. Réessayez avec Télécharger.");
      if (expired) return "expired" as const;
      if (job.value?.export_id === id) current.downloadFailed = true;
      return false;
    }
    finally {
      current.downloading = null;
    }
  }

  async function cancelExport() {
    if (!isActive.value || state.value.cancelling) return;
    const current = state.value;
    current.cancelling = true;
    try {
      await client(exportUrl.cancel(current.job!.export_id), { method: "DELETE", timeout: 15_000 });
      // Re-arm polling (see the monitor's `interrupted` watcher) so the cancellation gets confirmed.
      if (state.value === current) current.interrupted = false;
    }
    catch {
      current.cancelling = false;
      current.message = "L’annulation n’a pas pu être confirmée. Vérifiez l’état de l’export avant de réessayer.";
    }
  }

  /** Retry by server-owned export id, including notification items restored after reload. */
  async function retryExport(id: string) {
    if (state.value.retrying) return;
    const context = scope.value;
    const current = state.value;
    current.retrying = id;
    try {
      const response = await client.raw(exportUrl.retry(id), { method: "POST", timeout: 15_000 });
      if (!isCurrentScope(context)) return;
      if (response.status !== 202) throw new ExportResponseError();
      const data = await parseExportAck(response._data);
      if (!isCurrentScope(context)) return;
      trackExport(data, true);
    }
    catch (error) {
      if (!isCurrentScope(context)) return;
      const response = (error as { response?: { status: number; _data?: unknown } }).response;
      if (response?.status === 409) {
        try {
          const data = await parseExportAck(response._data, { conflict: true });
          if (!isCurrentScope(context)) return;
          trackExport(data, false, true);
          return;
        }
        catch {
          if (isCurrentScope(context)) rejectResponse();
          return;
        }
      }
      if (error instanceof ExportResponseError) {
        rejectResponse();
        return;
      }
      const expired = reportActionError(error, id, "L’export n’a pas pu être relancé. Réessayez ou revenez aux factures pour lancer un nouvel export.");
      if (expired) return "expired" as const;
    }
    finally {
      current.retrying = null;
    }
  }

  // useInvoiceExportMonitor owns timers and listeners; route components only attach jobs to serializable state.
  function monitorExport() {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let deadline: ReturnType<typeof setTimeout> | undefined;
    let generation = 0;
    let pending = false;
    let delayIndex = 0;
    let stopped = false;
    const delays = [3000, 5000, 10_000, 15_000];

    function clearTimers() {
      clearTimeout(timer);
      clearTimeout(deadline);
    }

    function schedule(delay: number) {
      clearTimeout(timer);
      if (!stopped && isActive.value && !state.value.interrupted && document.visibilityState !== "hidden") {
        timer = setTimeout(checkStatus, delay);
      }
    }

    // Only the initiating tab owns autoDownload; the attempt counts once the shared download slot is ours,
    // so a ready export waiting behind a bell download starts when that download ends.
    function autoDownload() {
      const current = state.value;
      if (job.value?.status !== "ready" || !current.autoDownload || current.autoAttempted || current.downloading) return;
      current.autoAttempted = true;
      return downloadExport(current.job!.export_id);
    }

    async function checkStatus() {
      if (pending || stopped || !isActive.value || state.value.interrupted || document.visibilityState === "hidden") return;
      const version = generation;
      const current = state.value;
      const id = current.job!.export_id;
      pending = true;
      try {
        const data = await client<unknown>(exportUrl.status(id), { timeout: 15_000 });
        if (version !== generation || stopped || current.interrupted || !isActive.value) return;
        const result = parseExportStatus(data, id);
        const jumped = result.processed - current.job!.processed > result.total * 0.1;
        current.job = result;
        current.message = "";
        current.conflict = false;
        if (!isActive.value) {
          clearTimers();
          current.cancelling = false;
          if (result.status === "ready" || result.status === "failed") $eventBus.emit("notifications:refresh");
          await autoDownload();
          return;
        }
        if (jumped) delayIndex = 0;
        schedule(delays[delayIndex]);
        delayIndex = Math.min(delayIndex + 1, delays.length - 1);
      }
      catch (error) {
        if (version !== generation || stopped || current.interrupted) return;
        if (error instanceof ExportResponseError) {
          rejectResponse();
          clearTimers();
          return;
        }
        const response = (error as { response?: { status: number; headers?: Headers } }).response;
        const status = response?.status;
        if (expireExport(error, id)) {
          current.cancelling = false;
          current.message = "";
          clearTimers();
          return;
        }
        current.message = "La connexion au suivi est interrompue. L’export peut continuer sur le serveur.";
        if (status && status < 500 && status !== 429) {
          stopTracking(current.message);
          clearTimers();
          return;
        }
        // A rate limit is recoverable: wait Retry-After seconds when given (capped at a minute), else back off.
        const retryAfter = status === 429 ? Number(response?.headers?.get("retry-after")) * 1000 : 0;
        schedule(retryAfter > 0 ? Math.min(retryAfter, 60_000) : delays[delayIndex]);
        delayIndex = Math.min(delayIndex + 1, delays.length - 1);
      }
      finally {
        if (version === generation) pending = false;
      }
    }

    function beginPolling() {
      clearTimers();
      generation++;
      pending = false;
      delayIndex = 0;
      if (!isActive.value || stopped || state.value.interrupted) return;
      // A hidden tab neither polls nor spends the budget; onVisibilityChange arms both on return.
      if (document.visibilityState !== "hidden") armDeadline();
      schedule(2000);
    }

    // The 30-minute budget counts visible tracking only; a hidden tab pauses it.
    function armDeadline() {
      clearTimeout(deadline);
      deadline = setTimeout(() => {
        if (state.value.interrupted) return;
        clearTimeout(timer);
        stopTracking("Le suivi s’est arrêté après 30 minutes. L’export peut continuer sur le serveur. Vérifiez son état ou consultez vos notifications.");
      }, 30 * 60_000);
    }

    function resume() {
      state.value.interrupted = false;
      return checkStatus();
    }

    const unwatch = watch([scope, () => job.value?.export_id, isActive], (_, previous) => {
      if (previous?.length && !isCurrentScope(previous[0])) {
        if (state.value.job) removeTask(state.value.job.export_id, state.value.scope);
        state.value = createInvoiceExportState(scope.value);
      }
      beginPolling();
    }, { immediate: true, flush: "sync" });

    const unpublish = watch(state, publishTask, { deep: true, flush: "sync" });
    const unregister = registerTaskKind("invoices", {
      cancel: task => isCurrentScope(task.scope) && task.id === job.value?.export_id ? cancelExport() : undefined,
      download: task => isCurrentScope(task.scope) ? downloadExport(task.id) : undefined,
      retry: (task) => {
        if (!isCurrentScope(task.scope)) return;
        if (isActive.value) return resume();
        return retryExport(task.id);
      },
    });

    // Every path that interrupts tracking (invalid answer, 4xx, deadline) leaves no timer armed;
    // every path that clears it (tray retry, 409 reattach) restarts polling here.
    const uninterrupted = watch(() => state.value.interrupted, (interrupted) => {
      if (interrupted) clearTimers();
      else beginPolling();
    }, { flush: "sync" });
    const unslot = watch(() => state.value.downloading, (busy) => {
      if (!busy) void autoDownload();
    }, { flush: "sync" });
    const uninvalid = watch(invalidSnapshot, (invalid) => {
      if (invalid) rejectResponse();
    }, { immediate: true, flush: "sync" });
    const unrestore = watch(activeTasks, (rows) => {
      // A finished job must not hide a newer active export; a stale row of itself must not revive it.
      if (isActive.value) return;
      const row = rows.find(task => task.type === "invoices" && isBackgroundTaskActive(task) && task.id !== job.value?.export_id);
      if (row) trackExport({ ...row, type: "invoices", export_id: row.id }, false, false, true);
    }, { immediate: true, flush: "sync" });

    function onVisibilityChange() {
      clearTimeout(timer);
      if (document.visibilityState === "hidden") {
        clearTimeout(deadline);
        return;
      }
      if (!stopped && isActive.value && !state.value.interrupted) armDeadline();
      void checkStatus();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return {
      stop: () => {
        stopped = true;
        generation++;
        clearTimers();
        unwatch();
        unpublish();
        unrestore();
        uninvalid();
        unslot();
        uninterrupted();
        unregister();
        document.removeEventListener("visibilitychange", onVisibilityChange);
      },
    };
  }

  return { actions: { job, downloading, retrying, scope, isCurrentScope, isActive, trackExport, rejectResponse, downloadExport, cancelExport, retryExport }, monitorExport };
}

/** One invoice export per user and tenant; only the initiating tab auto-downloads. */
export function useInvoiceExportJob() {
  return useInvoiceExportController().actions;
}

/**
 * App-level owner of the invoice export monitor, mounted once in app.vue so layout changes do not stop it.
 * Without a known export (started in this tab or listed in the bell's background_tasks) it adds no handler,
 * listener or request.
 */
export function useInvoiceExportMonitor() {
  const { actions: { job }, monitorExport } = useInvoiceExportController();
  const { activeTasks } = useBackgroundTasks();
  let monitor: ReturnType<typeof monitorExport> | undefined;
  onMounted(() => {
    watch(() => Boolean(job.value || activeTasks.value.some(row => row.type === "invoices")), (known) => {
      if (!monitor && known) monitor = monitorExport();
    }, { immediate: true, flush: "sync" });
  });
  onUnmounted(() => monitor?.stop());
}
